// @ts-nocheck
// @ts-ignore
/**
 * AddToKitModal - Manages the "Add to Kit" modal functionality.
 *
 * Handles:
 * - Fetching product data via Shopify Storefront API (GraphQL).
 * - Rendering product cards with "highlights" from metafields.
 * - Managing variant selection (including "Free Size" logic).
 * - Adding multiple items to cart via AJAX API.
 * - triggers cart drawer opening on success.
 */
class AddToKitModal {
    constructor() {
        this.cache = {};
        this.selectedVariants = {}; // Map of productId -> variantId
        this.products = [];

        // API Constants (Matching tabs-manager.js)
        this.SHOPIFY_STOREFRONT_ACCESS_TOKEN = window.storefrontAccessToken;
        this.SHOPIFY_DOMAIN = 'speedo-uat.myshopify.com';
        this.SHOPIFY_API_VERSION = '2026-01';

        // DOM Elements
        this.modalOverlay = document.querySelector('.atk-overlay');
        this.modalSheet = document.querySelector('.atk-modal');
        this.modalContent = document.querySelector('.atk-product-wrapper');
        this.closeButton = document.querySelector('.atk-close');
        this.addToCartButton = document.querySelector('.atk-button-wrapper button');
        this.heading = document.querySelector('.atk-heading');
        this.gestureBar = document.querySelector('.atk-gesture-bar');

        this.init();
    }

    init() {
        if (!this.modalOverlay) return;

        // Event Listeners
        this.closeButton?.addEventListener('click', () => this.close());
        this.modalOverlay.addEventListener('click', (e) => {
            if (e.target === this.modalOverlay) this.close();
        });

        this.addToCartButton?.addEventListener('click', () => this.addToCart());

        this.gestureBar?.addEventListener('click', () => this.close());

        // Mobile drag-to-dismiss (same behavior as add-to-card-modal setupGestureHandler)
        if (typeof window.setupGestureHandler === 'function' && this.modalSheet) {
            window.setupGestureHandler({
                gestureElementId: 'addToKitModalContent-gesture',
                modalContent: this.modalSheet,
                closeBtnId: 'addToKitModalCloseButton',
                onClose: () => this.close(),
            });
        }

        // Global access for other sections to call
        window.AddToKit = this;
    }

    clearSheetGestureStyles() {
        if (!this.modalSheet) return;
        this.modalSheet.style.animation = '';
        this.modalSheet.style.transform = '';
        this.modalSheet.style.transition = '';
    }

    /**
     * Opens the modal with the specified products.
     * @param {string[]} productIds - Array of product GIDs or IDs.
     * @param {string} [title] - Optional title for the modal.
     */
    async open(productIds, title = 'Build Your Kit') {
        this.clearSheetGestureStyles();
        this.products = [];
        this.selectedVariants = {};
        this.modalContent.innerHTML = '<div class="atk-loading" style="text-align:center; padding: 20px;">Loading...</div>';
        if (this.heading) this.heading.textContent = title;

        this.modalOverlay.classList.remove('modal-closed');
        this.modalOverlay.classList.add('modal-open');
        document.body.classList.add('overflow-hidden');

        try {
            const products = await this.fetchProducts(productIds);
            this.products = products;
            this.render(products);
        } catch (error) {
            console.error('Error loading kit products:', error);
            this.modalContent.innerHTML = '<div class="atk-error" style="text-align:center; padding: 20px;">Error loading products. Please try again.</div>';
        }
    }

    /**
     * Shows the modal without fetching/clearing content.
     * Assumes content is already rendered.
     * @param {string} [title]
     */
    show(title) {
        if (title && this.heading) this.heading.textContent = title;
        this.modalOverlay.classList.remove('modal-closed');
        this.modalOverlay.classList.add('modal-open');
        document.body.classList.add('overflow-hidden');
    }

    close() {
        this.clearSheetGestureStyles();
        this.modalOverlay.classList.add('modal-closed');
        this.modalOverlay.classList.remove('modal-open');
        document.body.classList.remove('overflow-hidden');
    }

    /**
     * Fetches product data from Shopify Storefront API.
     * @param {string[]} ids
     */
    async fetchProducts(ids) {
        // Ensure GID format
        const gids = ids.map(id => {
            const idStr = String(id);
            if (idStr.includes('gid://')) return idStr;
            return `gid://shopify/Product/${idStr}`;
        });

        const query = `
            query getProducts($ids: [ID!]!) {
                nodes(ids: $ids) {
                    ... on Product {
                        id
                        title
                        description
                        featuredImage {
                            url(transform: { maxWidth: 200 })
                        }
                        variants(first: 10) {
                            nodes {
                                id
                                title
                                availableForSale
                            }
                        }
                        metafield(namespace: "neo", key: "highlights") {
                            value
                        }
                    }
                }
            }
        `;

        const response = await fetch(`https://${window.storeDomain}/api/${this.SHOPIFY_API_VERSION}/graphql.json`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken,
            },
            body: JSON.stringify({
                query,
                variables: { ids: gids }
            }),
        });

        const json = await response.json();
        const products = json?.data?.nodes || [];

        return products;
    }

    /**
     * Determines if all products can be added directly to cart
     * without showing the modal.
     *
     * Rules:
     * - Every product must have variants.
     * - Exactly one available-for-sale variant per product.
     * - That single variant's title must be "Free Size" (case-insensitive).
     *
     * @param {Array<any>} products
     * @returns {boolean}
     */
    checkIfVariantAvailable(products) {
        if (!Array.isArray(products) || products.length === 0) return false;

        return products.every((product) => {
            if (!product || !product.variants || !Array.isArray(product.variants.nodes)) {
                return false;
            }

            const availableVariants = product.variants.nodes.filter(
                (v) => v && v.availableForSale
            );

            if (availableVariants.length !== 1) return false;

            const title = (availableVariants[0].title || '').trim().toLowerCase();

            return title === 'free size';
        });
    }

    render(products) {
        this.modalContent.innerHTML = '';

        products.forEach(product => {
            if (!product) return;

            // Initialize selection with first available variant
            const firstAvailable = product.variants.nodes.find(v => v.availableForSale) || product.variants.nodes[0];
            if (firstAvailable) {
                this.selectedVariants[product.id] = firstAvailable.id;
            }

            const card = document.createElement('div');
            card.className = 'atk-product-card flex';
            card.innerHTML = this.buildCardHTML(product);
            this.modalContent.appendChild(card);
        });

        // Re-attach listeners for new variant buttons
        this.modalContent.querySelectorAll('.atk-product-variant').forEach(btn => {
            btn.addEventListener('click', (e) => this.handleVariantClick(e));
        });
    }

    buildCardHTML(product) {
        const imageSrc = product.featuredImage?.url || '';
        const highlights = product.metafield?.value ? JSON.parse(product.metafield.value).join(' | ') : '';
        const variants = product.variants.nodes;

        // Determine if Single Variant / "Free Size"
        let variantHTML = '';
        const buttons = variants.map(v => {
            const isSelected = this.selectedVariants[product.id] === v.id ? 'active' : '';
            const disabledInfo = !v.availableForSale ? 'disabled' : '';

            return `<button class="atk-product-variant athletics-semibold ${isSelected}"
                      data-product-id="${product.id}"
                      data-variant-id="${v.id}"
                      ${disabledInfo}>${v.title}</button>`;
        }).join('');

        variantHTML = `
            <p class="atk-size-label athletics-medium text-black opacity-70">SIZE : </p>
            <div class="atk-product-variants flex gap-1">
                ${buttons}
            </div>
        `;

        return `
            <div class="atk-product-image">
                <img src="${imageSrc}" width="99" height="auto" loading="lazy">
            </div>
            <div class="atk-product-info">
                <p class="atk-product-name line-clamp-2 athletics-bold text-black">${product.title}</p>
                <p class="atk-product-description line-clamp-2 athletics-medium text-black opacity-70">${highlights}</p>
                <div class="atk-product-variants-wrapper flex gap-2 items-center">
                    ${variantHTML}
                </div>
            </div>
        `;
    }

    handleVariantClick(e) {
        const button = e.currentTarget;
        if (button.hasAttribute('disabled')) return;

        const productId = button.dataset.productId;
        const variantId = button.dataset.variantId;

        // Update state
        this.selectedVariants[productId] = variantId;

        // UI Update: find all buttons for this product and update active class
        const productCard = button.closest('.atk-product-card');
        productCard.querySelectorAll('.atk-product-variant').forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');
    }

    getCartSectionId() {
        // Try to find cart-items-component which usually holds the sectionId
        const cartItems = document.querySelector('cart-items-component');
        if (cartItems && cartItems.dataset.sectionId) {
            return cartItems.dataset.sectionId;
        }

        // Fallback: check other common selectors or return null
        // Cart drawer often has a specific section ID structure in Shopify themes
        const cartDrawerElement = document.querySelector('cart-drawer-component');
        if (cartDrawerElement) {
            // Sometimes the cart drawer itself IS the section or inside it.
            const section = cartDrawerElement.closest('.shopify-section');
            if (section) return section.id.replace('shopify-section-', '');
        }

        return 'cart-drawer'; // Reasonable default guess for standard themes
    }

    async addToCart() {
        const itemsToAdd = Object.values(this.selectedVariants).map(id => ({
            id: id.replace('gid://shopify/ProductVariant/', ''),
            quantity: 1
        }));

        if (itemsToAdd.length === 0) return;

        // Add loading state to button
        const originalText = this.addToCartButton.querySelector('.button-text').textContent;
        this.addToCartButton.querySelector('.button-text').textContent = 'Adding...';
        this.addToCartButton.style.opacity = '0.7';

        try {
            // Using Shopify AJAX API
            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ items: itemsToAdd })
            });

            if (response.ok) {
                // Run auditzyTrackingScript() if fetch was successful
                try {
                    if (typeof window !== 'undefined' && typeof window.auditzyTrackingScript === 'function') {
                        window.auditzyTrackingScript();
                    } else if (typeof auditzyTrackingScript === 'function') {
                        auditzyTrackingScript();
                    }
                } catch (err) {
                    console.warn('auditzyTrackingScript failed:', err);
                }

                const cartData = await response.json();

                // Close the modal
                this.close();

                // Open cart drawer
                const cartDrawer = document.querySelector('cart-drawer-component');
                if (cartDrawer && typeof cartDrawer.open === 'function') {
                    cartDrawer.open();
                }

                // Re-render cart section
                const sectionId = this.getCartSectionId();
                if (typeof window.reRenderCartSection === 'function') {
                    await window.reRenderCartSection(sectionId);
                }

                // Initialize choice goes well with if it exists
                if (window.choiceGoesWellWith && typeof window.choiceGoesWellWith.init === 'function' && cartData.items[0]) {
                    window.choiceGoesWellWith.init(cartData.items[0].product_id);
                }

                // Choice goes well with mobile/desktop content sync
                if (typeof window.syncMobileDesktopChoiceContent === 'function') {
                    window.syncMobileDesktopChoiceContent();
                }

                // Show all button activation
                if (typeof window.activateShowAllButton === 'function') {
                    window.activateShowAllButton();
                }

                // Wishlist re init
                if (window.customCartWishlistInstance && typeof window.customCartWishlistInstance.init === 'function') {
                    window.customCartWishlistInstance.init();
                }

                // Reset upsell products
                const upsellProducts = document.querySelector('upsell-products');
                if (upsellProducts && typeof upsellProducts.reset === 'function') {
                    upsellProducts.reset();
                }

            } else {
                console.error('Failed to add items to cart.');
            }
        } catch (err) {
            console.error(err);
            console.error('Error adding to cart.');
        } finally {
            this.addToCartButton.querySelector('.button-text').textContent = originalText;
            this.addToCartButton.style.opacity = '1';
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    new AddToKitModal();
});

